import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PageLayout from "@/components/PageLayout";
import { ReferenceNote } from "@/components/ReferenceNote";
import { newDossierReference } from "@/lib/reference";
import { VEILLE_COMMERCIALE } from "@/config/siteFlags";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function isNetlifyHost() {
  const host = window.location.hostname;
  return (
    host.endsWith(".netlify.app") ||
    host === "horsindex.com" ||
    host.endsWith(".horsindex.com")
  );
}

const EMPTY = { prenom: "", nom: "", email: "", sujet: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [challenge, setChallenge] = useState(null); // server mode {token, question}
  const [local, setLocal] = useState(null); // netlify mode {a, b}
  const [answer, setAnswer] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [reference, setReference] = useState("");

  const netlify = isNetlifyHost();

  const loadChallenge = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contact-challenge`);
      setChallenge(res.data);
      setAnswer("");
    } catch {
      setLocal({
        a: Math.floor(Math.random() * 9) + 1,
        b: Math.floor(Math.random() * 9) + 1,
      });
    }
  }, []);

  useEffect(() => {
    if (netlify) {
      setLocal({
        a: Math.floor(Math.random() * 9) + 1,
        b: Math.floor(Math.random() * 9) + 1,
      });
    } else {
      loadChallenge();
    }
  }, [netlify, loadChallenge]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.prenom.trim() || !form.nom.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Merci de renseigner votre prénom, votre nom, votre e-mail et votre message.");
      return;
    }
    if (!consent) {
      toast.error("Merci de cocher la case de consentement.");
      return;
    }
    if (answer.trim() === "") {
      toast.error("Merci de répondre à la question anti-spam.");
      return;
    }
    if (local && Number(answer) !== local.a + local.b) {
      toast.error("Le résultat de la vérification anti-spam est incorrect.");
      setLocal({
        a: Math.floor(Math.random() * 9) + 1,
        b: Math.floor(Math.random() * 9) + 1,
      });
      setAnswer("");
      return;
    }
    setSending(true);
    const ref = newDossierReference();
    try {
      if (netlify) {
        const fd = new URLSearchParams();
        fd.append("form-name", "contact");
        fd.append("origine", "contact");
        fd.append("reference", ref);
        fd.append("prenom", form.prenom);
        fd.append("nom", form.nom);
        fd.append("email", form.email);
        fd.append("sujet", form.sujet);
        fd.append("message", form.message);
        fd.append("consentement", "oui");
        await fetch("/", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: fd.toString(),
        }).then((r) => {
          if (!r.ok) throw new Error(`netlify-form-error-${r.status}`);
        });
      } else {
        await axios.post(`${API}/contact`, {
          nom: form.nom,
          prenom: form.prenom,
          email: form.email,
          sujet: form.sujet || null,
          message: form.message,
          reference: ref,
          consentement: true,
          website: honeypot,
          captcha_token: challenge?.token || "",
          captcha_answer: answer,
        });
      }
      setReference(ref);
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "L'envoi a échoué. Merci de réessayer dans un instant."
      );
      if (!netlify) loadChallenge();
    } finally {
      setSending(false);
    }
  };

  const question = challenge ? challenge.question : local ? `${local.a} + ${local.b}` : "…";

  return (
    <PageLayout
      eyebrow="Contact"
      title="Contact"
      intro={
        VEILLE_COMMERCIALE
          ? "Pour me contacter au sujet de HorsIndex, de ses contenus ou pour toute autre demande, vous pouvez utiliser ce formulaire."
          : "Vous avez repéré des informations sur vous en ligne et vous souhaitez savoir ce qu'il est possible de faire ? Écrivez à HorsIndex. Pour une évaluation complète de votre situation, l'audit initial reste le point d'entrée dédié."
      }
      hideFooterCta
    >
      <section className="bg-[#FAF9F7]">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pb-24 lg:pb-32">
          {sent ? (
            <div
              data-testid="contact-success"
              className="bg-white rounded-md border border-[#E2DEDA] p-8 lg:p-10"
            >
              <p className="label-eyebrow mb-4">Message envoyé</p>
              <p className="font-serif text-2xl text-[#181A1D] font-normal leading-snug mb-4">
                Merci pour votre message.
              </p>
              <p className="text-[15px] text-[#5A5E64] font-normal leading-relaxed">
                HorsIndex vous répond à l'adresse indiquée, généralement sous
                quelques jours ouvrés.
              </p>
              <ReferenceNote reference={reference} testId="contact-reference" className="mt-6" />
              <Link
                to="/"
                data-testid="contact-back-home"
                className="mt-8 inline-block text-[11px] tracking-[0.18em] uppercase font-medium text-[#181A1D] underline decoration-[#D8D3CD] underline-offset-4 hover:decoration-[#181A1D] transition-colors"
              >
                Retour à l'accueil
              </Link>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-white rounded-md border border-[#E2DEDA] p-8 lg:p-10 space-y-7"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                <div>
                  <label htmlFor="contact-prenom" className="label-eyebrow block mb-3">
                    Prénom *
                  </label>
                  <input
                    id="contact-prenom"
                    type="text"
                    value={form.prenom}
                    onChange={set("prenom")}
                    data-testid="contact-prenom"
                    className="input-line w-full"
                    autoComplete="given-name"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="contact-nom" className="label-eyebrow block mb-3">
                    Nom *
                  </label>
                  <input
                    id="contact-nom"
                    type="text"
                    value={form.nom}
                    onChange={set("nom")}
                    data-testid="contact-nom"
                    className="input-line w-full"
                    autoComplete="family-name"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-email" className="label-eyebrow block mb-3">
                  Adresse e-mail *
                </label>
                <input
                  id="contact-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  data-testid="contact-email"
                  className="input-line w-full"
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label htmlFor="contact-sujet" className="label-eyebrow block mb-3">
                  Objet
                </label>
                <input
                  id="contact-sujet"
                  type="text"
                  value={form.sujet}
                  onChange={set("sujet")}
                  data-testid="contact-sujet"
                  className="input-line w-full"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="label-eyebrow block mb-3">
                  Message *
                </label>
                <textarea
                  id="contact-message"
                  rows={6}
                  required
                  value={form.message}
                  onChange={set("message")}
                  data-testid="contact-message"
                  className="input-line w-full resize-y"
                />
              </div>

              {/* Honeypot — invisible pour les visiteurs */}
              <div className="hidden" aria-hidden="true">
                <label htmlFor="contact-website">Ne pas remplir</label>
                <input
                  id="contact-website"
                  type="text"
                  name="bot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              <div className="border-t border-[#E2DEDA] pt-6">
                <p className="label-eyebrow mb-3">Vérification anti-spam</p>
                <div className="flex flex-wrap items-center gap-4">
                  <span
                    data-testid="contact-captcha-question"
                    className="font-serif text-xl text-[#181A1D] tracking-tight select-none"
                  >
                    {question} =
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value.replace(/[^0-9-]/g, ""))}
                    data-testid="contact-captcha-input"
                    className="input-line max-w-[6rem]"
                    placeholder="?"
                    aria-label="Vérification anti-spam"
                  />
                </div>
              </div>

              <label
                htmlFor="contact-consent"
                className="flex items-start gap-3 cursor-pointer"
              >
                <input
                  id="contact-consent"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="contact-consent"
                  className="mt-1 h-4 w-4 accent-[#E94B3C]"
                />
                <span className="text-[13px] text-[#5A5E64] font-normal leading-relaxed">
                  J'accepte que les informations transmises soient utilisées
                  uniquement pour répondre à ma demande.{" "}
                  <Link
                    to="/politique-confidentialite"
                    className="underline decoration-[#D8D3CD] underline-offset-4 text-[#181A1D] hover:decoration-[#181A1D] transition-colors"
                  >
                    Politique de confidentialité
                  </Link>
                  .
                </span>
              </label>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="btn-primary disabled:opacity-50"
              >
                {sending ? "Envoi…" : "Envoyer le message"}
              </button>
            </form>
          )}

          <div
            data-testid="contact-channels"
            className="mt-8 text-[13px] text-[#666A70] font-normal leading-relaxed"
          >
            <p>
              E-mail :{" "}
              <a
                href="mailto:horsindex@gmail.com"
                data-testid="contact-email-link"
                className="text-[#33363B] underline decoration-[#D8D3CD] underline-offset-4 hover:text-[#181A1D] hover:decoration-[#181A1D] transition-colors"
              >
                horsindex@gmail.com
              </a>
            </p>
            {VEILLE_COMMERCIALE ? null : (
              <p className="mt-1.5">
                Téléphone professionnel :{" "}
                <a
                  href="tel:+33608033203"
                  data-testid="contact-phone-link"
                  className="text-[#33363B] underline decoration-[#D8D3CD] underline-offset-4 hover:text-[#181A1D] hover:decoration-[#181A1D] transition-colors"
                >
                  06 08 03 32 03
                </a>
              </p>
            )}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
